import React from 'react';
import {
  Menu,
  MenuProps,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Button,
  Chip,
  IconButton,
} from '@mui/material';
import {
  Info,
  Warning,
  Error,
  CheckCircle,
  Close,
  MarkEmailRead,
} from '@mui/icons-material';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import {
  selectNotifications,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  removeNotification,
} from '@/store/slices/uiSlice';
import { formatDistanceToNow } from 'date-fns';

interface NotificationPanelProps extends Omit<MenuProps, 'children'> {}

const getNotificationIcon = (type: string) => {
  switch (type) {
    case 'success':
      return <CheckCircle sx={{ color: 'success.main' }} />;
    case 'warning':
      return <Warning sx={{ color: 'warning.main' }} />;
    case 'error':
      return <Error sx={{ color: 'error.main' }} />;
    default:
      return <Info sx={{ color: 'info.main' }} />;
  }
};

export const NotificationPanel: React.FC<NotificationPanelProps> = (props) => {
  const dispatch = useAppDispatch();
  const notifications = useAppSelector(selectNotifications);

  const handleMarkAsRead = (id: string, event: React.MouseEvent) => {
    event.stopPropagation();
    dispatch(markNotificationAsRead(id));
  };

  const handleRemove = (id: string, event: React.MouseEvent) => {
    event.stopPropagation();
    dispatch(removeNotification(id));
  };

  const handleMarkAllAsRead = () => {
    dispatch(markAllNotificationsAsRead());
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <Menu
      {...props}
      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      PaperProps={{
        sx: {
          mt: 1,
          width: 400,
          maxHeight: 500,
        },
      }}
    >
      {/* Header */}
      <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Notifications
          </Typography>
          {unreadCount > 0 && (
            <Chip
              label={`${unreadCount} new`}
              size="small"
              color="primary"
              variant="outlined"
            />
          )}
        </Box>
        {unreadCount > 0 && (
          <Button
            size="small"
            startIcon={<MarkEmailRead />}
            onClick={handleMarkAllAsRead}
            sx={{ mt: 1 }}
          >
            Mark all as read
          </Button>
        )}
      </Box>

      {/* Notifications List */}
      <List disablePadding sx={{ maxHeight: 400, overflow: 'auto' }}>
        {notifications.length === 0 ? (
          <ListItem>
            <ListItemText
              primary="No notifications"
              secondary="You're all caught up!"
              sx={{ textAlign: 'center' }}
            />
          </ListItem>
        ) : (
          notifications.map((notification) => (
            <React.Fragment key={notification.id}>
              <ListItem
                sx={{
                  backgroundColor: notification.read ? 'transparent' : 'action.hover',
                  '&:hover': {
                    backgroundColor: 'action.selected',
                  },
                }}
              >
                <ListItemIcon sx={{ alignSelf: 'flex-start', mt: 0.5 }}>
                  {getNotificationIcon(notification.type)}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: notification.read ? 400 : 600,
                          flex: 1,
                          mr: 1,
                        }}
                      >
                        {notification.title}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {!notification.read && (
                          <IconButton
                            size="small"
                            onClick={(e) => handleMarkAsRead(notification.id, e)}
                            sx={{ p: 0.25 }}
                          >
                            <MarkEmailRead fontSize="small" />
                          </IconButton>
                        )}
                        <IconButton
                          size="small"
                          onClick={(e) => handleRemove(notification.id, e)}
                          sx={{ p: 0.25 }}
                        >
                          <Close fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 0.5 }}
                      >
                        {notification.message}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDistanceToNow(notification.timestamp, { addSuffix: true })}
                      </Typography>
                    </Box>
                  }
                />
              </ListItem>
              <Divider />
            </React.Fragment>
          ))
        )}
      </List>

      {notifications.length > 0 && (
        <Box sx={{ p: 1, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button fullWidth variant="text" size="small">
            View All Notifications
          </Button>
        </Box>
      )}
    </Menu>
  );
};